import React, { useState } from "react";
import styled from "@emotion/styled";
import { Route, Routes } from "react-router-dom";
import List, { ListItem, ListItemText } from "~/components/list";
import { useBreadcrumbs } from "~/main-layout/BreadcrumbProvider";
import { Dialog, DialogTitle } from "~/components/dialog";
import TextFormField from "~/components/text-form-field";
import FormBottomRow from "~/components/form-bottom-row";
import { useSnackBar } from "~/components/snackbar";
import { ManageRefDataPage } from "~/manage-ref-data";
import DropdownFormField from "~/visuals/organisms/DropdownFormField";
import { ErrorRenderer, MutationForm } from "~/components/forms/MutationForm";
import { ValueRenderer } from "~/components/forms/ValueContext";
import { Body2 } from "@material/react-typography";
import { useRelativeNav } from "~/relative-nav";
import { ValidationResponse } from "~/gql/types";
import {
  CommandAuthorization,
  PriceClassification,
  ProductUnit,
} from "~/gql/types2";
import { useUser } from "~/Auth/useUser";
import { ProductPage } from "./productSingle";
import { ProductsProvider, useProducts } from "./ProductsProvider";
import { classificationLabel, overrideRates, Product } from "./shared";

type NewProductState = {
  name: string;
  // Null until the user picks: creating a product should not presume a
  // classification or unit, both of which are frozen after creation.
  classification: PriceClassification | null;
  unit: ProductUnit | null;
  dialogClosing: boolean;
};

// `section.form-field` carries `margin: 16px`, so match that on the left to
// line the text up with the fields above and below it.
const ClassificationHelp = styled(Body2)`
  margin: 0 16px 12px;
`;

const FieldSeparator = styled("hr")`
  margin: 0 16px 16px;
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
`;

// Options come from the generated enums rather than a GraphQL introspection
// query: these types live in the HotChocolate schema, which
// `EnumDropdownFormField` cannot reach (it introspects the older endpoint).
//
// Only the catalogued classifications are creatable -- `PerDiem` and
// `EmployeeRate` products take their amounts from outside the catalog and are
// minted by migration, and the handler rejects them whatever the UI offers.
// ENUMERATED DELIBERATELY, not filtered off the full set: "all except these
// two" would silently offer any classification added later.
const creatableClassifications = [
  PriceClassification.Fixed,
  PriceClassification.Variable,
] as const;

const classificationOptions = creatableClassifications.map((x) => ({
  label: x,
  value: x,
}));

/**
 * The list's one-line summary. Only `Fixed` and `PerDiem` have rates worth
 * counting -- a fixed product's catalogued prices and a per diem product's
 * overrides. `Variable` and `EmployeeRate` carry nothing but their sentinel,
 * which is an implementation detail rather than a price.
 */
const summarize = (product: Product): string => {
  const label = classificationLabel[product.classification];

  if (product.classification === PriceClassification.Fixed) {
    return `${label} — ${product.rates.length} rate(s)`;
  }

  if (product.classification === PriceClassification.PerDiem) {
    return `${label} — ${overrideRates(product).length} override(s)`;
  }

  return label;
};

type CreatableClassification = (typeof creatableClassifications)[number];

const isCreatable = (
  classification: PriceClassification | null
): classification is CreatableClassification =>
  !!classification &&
  (creatableClassifications as readonly PriceClassification[]).includes(
    classification
  );

const unitOptions = Object.values(ProductUnit).map((x) => ({
  label: x,
  value: x,
}));

// Keyed to the creatable classifications only -- the form offers no others, so
// there is nothing to explain for the externally-sourced two.
const classificationHelp: Record<CreatableClassification, string> = {
  [PriceClassification.Fixed]:
    "Fixed - The price per unit never changes. Use this for items where a specific numeric rate is always specified.",
  [PriceClassification.Variable]:
    "Variable - The price is variable due to the underlying billable item being impossible to quantify ahead of time. Use this for things like receipt-valued expenses and third-party invoices.",
};

const ProductsList: React.FC = () => {
  useBreadcrumbs([{ text: "Products" }], []);

  const addAlert = useSnackBar();
  const { commands } = useUser();
  const nav = useRelativeNav();
  const { products, saving, createProduct } = useProducts();
  const { creating } = saving;

  const canAddProducts = commands.has(
    CommandAuthorization.CreateProductCommand
  );

  const title = `Showing ${products.length} Products`;

  const [newProduct, setNewProduct] = useState<NewProductState | null>(null);

  const onAddNewClick = () => {
    // The button is not rendered at all without the authorization (see
    // `canAddNew` below), so this is a backstop rather than the gate: it keeps
    // the dialog closed if the page is ever wired to another trigger.
    if (!canAddProducts) {
      return;
    }
    setNewProduct({
      name: "",
      classification: null,
      unit: null,
      dialogClosing: false,
    });
  };

  const onProductSuccess = () => {
    addAlert({
      key: `${Math.random()}`,
      message: "Saved Product",
      isSuccess: true,
    });
    setNewProduct(null);
  };

  const runProductMutation = async (
    values: NewProductState
  ): Promise<ValidationResponse> => {
    // The dropdowns are `required`, so the form blocks submit until both are
    // picked; this keeps the mutation input honestly non-null for TypeScript.
    if (!values.classification || !values.unit) {
      return {
        errors: [
          {
            message: "Price classification and unit are required.",
            argumentName: null,
          },
        ],
      } as ValidationResponse;
    }

    return createProduct({
      name: values.name,
      classification: values.classification,
      unit: values.unit,
    });
  };

  return (
    <ManageRefDataPage
      {...{
        title,
        onAddNewClick,
        canAddNew: canAddProducts,
        loading: creating,
        isEmpty: products.length === 0,
        emptyMessage: canAddProducts
          ? "No products yet. Use Add New to create the first one."
          : "No products yet.",
      }}
    >
      <List twoLine>
        {products
          .slice()
          .sort((a, b) => (a.name > b.name ? 1 : -1))
          .map((item) => (
            <ListItem key={item.id} onClick={() => nav(`${item.id}`)}>
              <ListItemText
                primaryText={`${item.name} (${item.unit})`}
                secondaryText={summarize(item)}
              />
            </ListItem>
          ))}
      </List>

      <Dialog
        open={!!newProduct && !newProduct.dialogClosing}
        onClose={() => setNewProduct(null)}
      >
        <DialogTitle>Create Product</DialogTitle>
        <MutationForm
          initialValues={newProduct ?? ({} as NewProductState)}
          runMutation={runProductMutation}
          onSuccess={onProductSuccess}
        >
          <TextFormField
            formField="name"
            label="Name"
            helperText="The product name"
            required
          />
          <DropdownFormField
            formField="classification"
            label="Price Classification"
            helperText="Fixed or Variable — cannot be changed after creation"
            options={classificationOptions}
            required
          />
          <ValueRenderer
            render={(values) => {
              // The dropdown offers only the creatable two, so anything else is
              // unreachable -- but the lookup has to be narrowed for it.
              const help = isCreatable(values.classification)
                ? classificationHelp[values.classification]
                : undefined;

              return help ? (
                <ClassificationHelp>{help}</ClassificationHelp>
              ) : (
                <></>
              );
            }}
          />
          <FieldSeparator />
          <DropdownFormField
            formField="unit"
            label="Unit"
            helperText="The unit of measure — cannot be changed after creation"
            options={unitOptions}
            required
          />

          <ErrorRenderer
            render={(error) => (
              <FormBottomRow
                buttonText="Save"
                loading={creating}
                onCancel={() =>
                  setNewProduct((x) => ({ ...x!, dialogClosing: true }))
                }
                errorMessage={error}
              />
            )}
          />
        </MutationForm>
      </Dialog>
    </ManageRefDataPage>
  );
};

export const Products: React.FC = () => (
  <ProductsProvider>
    <Routes>
      <Route path=":id" element={<ProductPage />} />
      <Route index element={<ProductsList />} />
    </Routes>
  </ProductsProvider>
);
